import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import { Box } from "@stenajs-webui/core";
import { StandardTableHtmlCopyToClipboardButton } from "@stenajs-webui/grid-export";
import { useMemo } from "react";
import { createRebateBookingsSummaryTableConfig } from "../../config/RebateBookingsSummaryTableConfig";
import {
  createRebateBookingsHeaderRows,
  groupReservedBookingsByRoute,
} from "../../lib/reservedBookingUtils";
import { RebateBookingsOverview } from "../../types/types";

interface Props {
  rebate: RebateBookingsOverview;
}

/**
 * Copies one summary row per route (route code, booking count, total
 * reserved), matching the collapsed route cards rather than every booking,
 * under the same header the summary card shows.
 */
export const RebateBookingsCopyButton = ({ rebate }: Props) => {
  const routes = useMemo(() => groupReservedBookingsByRoute(rebate), [rebate]);
  const config = useMemo(
    () =>
      createRebateBookingsSummaryTableConfig(
        rebate.rebateAgreement?.currencyCode
      ),
    [rebate.rebateAgreement?.currencyCode]
  );
  const headerRows = useMemo(
    () => createRebateBookingsHeaderRows(rebate, config.columnOrder.length),
    [rebate, config]
  );

  if (routes.length === 0) {
    return null;
  }

  return (
    // The button comes from grid-export and takes no data-testid of its own,
    // so the id goes on a wrapper.
    <Box data-testid={testIdConstants.rebateBookingsCopyButton}>
      <StandardTableHtmlCopyToClipboardButton
        config={config}
        items={routes}
        // The header belongs in the table's own head, so it travels with the
        // table wherever the paste lands. A function replacement keeps $ in an
        // agreement name from being read as a replacement pattern.
        renderContent={(table) =>
          table.replace("<thead>", () => `<thead>${headerRows}`)
        }
        label={"Copy to clipboard"}
        labelAfterCopy={"Copied!"}
      />
    </Box>
  );
};
